import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { BasicCRMModal } from 'crm/_components';
import { useContactsFilters } from './ContactsFiltersContext';
import { getCondition, PAYMENT_PERIOD, validCondition } from './model';
import SavedContactFilters from './SavedContactFilters';
import { staticStyles } from './style';

const choiceLabel = (choice, intl) =>
  intl.messages[choice.name] ? intl.formatMessage({ id: choice.name }) : choice.name;

const FilterEditor = ({ initialKey }) => {
  const { filters, text, params, change, setEditor, intl } = useContactsFilters();
  const [field, setField] = useState(initialKey || Object.keys(filters)[0]);
  const initialCondition = key => {
    const value = getCondition(filters[key], params) || { operator: filters[key].operators[0], values: [] };
    return filters[key].inputType === 'inputDate'
      ? { ...value, values: value.values.map(date => date.slice(0, 10)) }
      : value;
  };
  const [condition, setCondition] = useState(initialCondition(field));
  const [search, setSearch] = useState('');
  const [invalid, setInvalid] = useState(false);
  const filter = filters[field];
  const isChoice = ['inputIntegerChoice', 'inputStringChoice'].includes(filter.inputType);
  const isRange = ['inputDate', 'inputRange'].includes(filter.inputType);
  const inputType = { inputDate: 'date', inputRange: 'number' }[filter.inputType] || 'text';
  const isBetween = ['between', 'not_between'].includes(condition.operator);
  const setValue = (value, index = 0) => {
    const values = isRange && isBetween ? [condition.values[0] || '', condition.values[1] || ''] : [''];
    values[index] = value;
    setCondition({ ...condition, values });
  };
  const submit = event => {
    event.preventDefault();
    if (getCondition(filter, params) && JSON.stringify(condition) === JSON.stringify(initialCondition(field))) {
      setEditor(null);
      return;
    }
    if (!validCondition(filter, condition)) {
      setInvalid(true);
      return;
    }
    change(field, condition);
    setEditor(null);
  };
  return (
    <form className="ContactsFilters__editor" onSubmit={submit}>
      <label>
        {text('field')}
        <select
          value={field}
          onChange={event => {
            setField(event.target.value);
            setCondition(initialCondition(event.target.value));
            setInvalid(false);
            setSearch('');
          }}>
          {Object.keys(filters).map(key => (
            <option key={key} value={key}>
              {text(key)}
            </option>
          ))}
        </select>
      </label>
      <label>
        {text('operation')}
        <select
          value={condition.operator}
          onChange={event => {
            const operator = event.target.value;
            const count = ['between', 'not_between'].includes(operator) ? 2 : 1;
            let values = isRange ? condition.values.slice(0, count) : condition.values;
            if (field === PAYMENT_PERIOD && operator === 'lte') values = condition.values.slice(-1);
            if (field === PAYMENT_PERIOD && operator === 'between' && condition.operator === 'lte')
              values = ['', condition.values[0] || ''];
            setCondition({ operator, values });
            setInvalid(false);
          }}>
          {filter.operators.map(operator => (
            <option key={operator} value={operator}>
              {text(filter.operatorLabels?.[operator] || operator)}
            </option>
          ))}
        </select>
      </label>
      {isChoice && (
        <fieldset>
          <legend>{text('value')}</legend>
          <input
            type="search"
            aria-label={text('searchChoices')}
            placeholder={text('searchChoices')}
            value={search}
            onChange={event => setSearch(event.target.value)}
          />
          <div className="ContactsFilters__choices">
            {filter.choiceValues
              .filter(choice => String(choiceLabel(choice, intl)).toLowerCase().includes(search.toLowerCase()))
              .map(choice => (
                <label key={choice.value}>
                  <input
                    type="checkbox"
                    checked={condition.values.includes(String(choice.value))}
                    onChange={event =>
                      setCondition({
                        ...condition,
                        values: event.target.checked
                          ? [...condition.values, String(choice.value)]
                          : condition.values.filter(value => value !== String(choice.value)),
                      })
                    }
                  />
                  {choiceLabel(choice, intl)}
                </label>
              ))}
            {!filter.choiceValues.length && <p>{text('noChoices')}</p>}
          </div>
        </fieldset>
      )}
      {!isChoice && filter.inputType === 'inputBoolean' && (
        <label>
          {text('value')}
          <select
            value={condition.values[0]?.toLowerCase() || ''}
            onChange={event => setValue(event.target.value)}
            required>
            <option value="" disabled>
              —
            </option>
            <option value="true">{intl.formatMessage({ id: 'justYes' })}</option>
            <option value="false">{intl.formatMessage({ id: 'justNo' })}</option>
          </select>
        </label>
      )}
      {!isChoice && filter.inputType !== 'inputBoolean' && (
        <div className="ContactsFilters__range">
          {(isRange && isBetween ? [0, 1] : [0]).map(index => (
            <label key={index}>
              {text(isRange && isBetween ? ['from', 'to'][index] : 'value')}
              <input
                type={inputType}
                step={filter.inputType === 'inputRange' ? 'any' : undefined}
                required
                value={condition.values[index] || ''}
                onChange={event => setValue(event.target.value, index)}
              />
            </label>
          ))}
        </div>
      )}
      <p className="ContactsFilters__hint">
        {text(field === PAYMENT_PERIOD ? 'crmContactFiltersPaymentPeriodHint' : 'all')}
      </p>
      {invalid && (
        <p role="alert" className="ContactsFilters__error">
          {text('invalid')}
        </p>
      )}
      <div className="ContactsFilters__buttons">
        {getCondition(filter, params) && (
          <button
            type="button"
            onClick={() => {
              change(field, null);
              setEditor(null);
            }}>
            {text('remove')}
          </button>
        )}
        <button type="button" onClick={() => setEditor(null)}>
          {text('cancel')}
        </button>
        <button type="submit" className="ContactsFilters__primary">
          {text('apply')}
        </button>
      </div>
    </form>
  );
};

export const ColumnFilterHeader = ({ filterKey, label }) => {
  const { text, filters, params, setEditor, loading } = useContactsFilters();
  const active = filters[filterKey] && getCondition(filters[filterKey], params);
  return (
    <span className="ContactsFilters__column">
      {label}
      <button
        type="button"
        className={`ContactsFilters__column-button${active ? ' is-active' : ''}`}
        title={`${text('filters')}: ${text(filterKey)}`}
        aria-label={`${text('filters')}: ${text(filterKey)}`}
        aria-pressed={!!active}
        disabled={loading || !filters[filterKey]}
        onClick={() => setEditor(filterKey)}>
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path d="M2 3h12L9.5 8v4L6.5 14V8L2 3Z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
        </svg>
      </button>
    </span>
  );
};

const ContactsFilters = () => {
  const { text, intl, params, filters, loading, error, editor, active, setEditor, change, reset, replace, load } =
    useContactsFilters();
  const describe = ({ filter, condition }) =>
    condition.values
      .map(value => {
        if (filter.inputType === 'inputBoolean')
          return intl.formatMessage({ id: value.toLowerCase() === 'true' ? 'justYes' : 'justNo' });
        const choice = filter.choiceValues.find(
          item => item && typeof item === 'object' && String(item.value) === value
        );
        return choice ? choiceLabel(choice, intl) : value;
      })
      .join(['between', 'not_between'].includes(condition.operator) ? ' — ' : ', ');
  const hasFilters = Object.keys(params).some(key => !['page', 'page_size', 'ordering'].includes(key));
  return (
    <div className="ContactsFilters">
      <SavedContactFilters />
      <div className="ContactsFilters__toolbar">
        <button type="button" disabled={loading || !Object.keys(filters).length} onClick={() => setEditor('')}>
          + {text('add')}
        </button>
        {hasFilters && (
          <button type="button" className="ContactsFilters__danger" onClick={reset}>
            {text('reset')}
          </button>
        )}
      </div>
      {loading && <p role="status">{text('loading')}</p>}
      {error && (
        <p role="alert" className="ContactsFilters__error">
          {error}{' '}
          {!Object.keys(filters).length && (
            <button type="button" onClick={load}>
              {text('retry')}
            </button>
          )}
        </p>
      )}
      <div className="ContactsFilters__active">
        {active.map(item => (
          <span className="ContactsFilters__chip" key={item.key}>
            <button type="button" onClick={() => setEditor(item.key)}>
              <strong>{text(item.key)}</strong>{' '}
              {text(item.filter.operatorLabels?.[item.condition.operator] || item.condition.operator).toLowerCase()}:{' '}
              {describe(item)}
            </button>
            <button
              type="button"
              aria-label={`${text('remove')}: ${text(item.key)}`}
              onClick={() => change(item.key, null)}>
              ×
            </button>
          </span>
        ))}
        {params.search && (
          <span className="ContactsFilters__chip">
            <span>
              {text('search')}: {params.search}
            </span>
            <button
              type="button"
              aria-label={`${text('remove')}: ${text('search')}`}
              onClick={() => replace({ ...params, search: undefined, page: 1 })}>
              ×
            </button>
          </span>
        )}
      </div>
      <BasicCRMModal
        isOpen={editor !== null && !!Object.keys(filters).length}
        className="ContactsFiltersModal"
        overlayClassName="ContactsFiltersOverlay"
        caption={text('filters')}
        onRequestClose={() => setEditor(null)}>
        {editor !== null && !!Object.keys(filters).length && <FilterEditor key={editor} initialKey={editor} />}
      </BasicCRMModal>
      <style jsx global>
        {staticStyles}
      </style>
    </div>
  );
};

FilterEditor.propTypes = { initialKey: PropTypes.string.isRequired };
ColumnFilterHeader.propTypes = { filterKey: PropTypes.string.isRequired, label: PropTypes.node.isRequired };
export default ContactsFilters;
